import React from 'react';

import Link from '../../../../../../library/link/link';
import Typography from '../../../../../../library/typography/typography';
import Price from '../../../../../../components/price/price';
import ProductImage from '../../../../../../components/product-image/product-image';
import CompareCheckbox from '../../../../components/compare-checkbox/compare-checkbox';
import TopThreeTileHeader from '../top-three-tile-header/top-three-tile-header';

import { useProductQuantity } from '../../../../../../shared/hooks/hooks';

import {
  getNormalizedProductType,
  getPriceInfo,
  getProductImage
} from '../../../../../../shared/utils/product/product';

import {
  BundleType,
  ProductData,
  ProductTypeEnum
} from '../../../../../../shared/types/graphql-types';
import { PRODUCT_IMAGE_FORMATS } from '../../../../../../shared/constants/product-images';
import { PRODUCT_TILE_TYPES } from '../../../../../../shared/constants/treadwell';

import './top-three-tile.scss';

interface TopThreeTileProps {
  onViewDetailsClick?: (value: string) => void;
  product: ProductData;
  type: typeof PRODUCT_TILE_TYPES[keyof typeof PRODUCT_TILE_TYPES];
}

//TODO: Attempt to use ProductTile to make TopThreeTile and SuggestedSellingTile consistent
function TopThreeTile(props: TopThreeTileProps) {
  const { onViewDetailsClick, product, type } = props;

  const {
    brand,
    mapDisplayRule,
    mapRuleSatisfied,
    name = '',
    rearProduct,
    recommendationData,
    unitName,
    url = ''
  } = product || {};

  const { rank } = recommendationData || {};
  const isTreadwell = Boolean(rank && rank > 0);

  const priceInfo = getPriceInfo(product);
  const rearPriceInfo = getPriceInfo(rearProduct);
  const { mapPrice, price, priceRange } = priceInfo || {};
  const {
    mapPrice: rearMapPrice,
    price: rearPrice,
    priceRange: rearPriceRange
  } = rearPriceInfo || {};
  const priceSubtext =
    mapRuleSatisfied &&
    mapDisplayRule?.messageDisplay &&
    mapDisplayRule?.mapPriceDisplay
      ? product.mapDisplayRule?.message
      : '';

  const rearCode = rearProduct?.code;

  const normalizedProductType =
    getNormalizedProductType(product) || ProductTypeEnum.Tires;
  const { quantity, rearQuantity } = useProductQuantity({
    articleServiceType: product?.articleServiceType,
    bundleType: product?.rearProduct ? BundleType.StaggeredSet : undefined,
    productType: normalizedProductType
  });

  const imageUrl = getProductImage(product, {
    format:
      process.env.__FF_PRODUCT_IMAGE_FORMATS_AUTO_SYNC__ === 'true'
        ? PRODUCT_IMAGE_FORMATS.PRODUCT_X_SMALL
        : PRODUCT_IMAGE_FORMATS.LIST,
    shouldFallBackToFirst: true
  })?.url;

  return (
    <div styleName={rearCode ? 'staggered-tile' : 'tile'}>
      <div styleName="inner-tile">
        <TopThreeTileHeader isTreadwell={isTreadwell} rank={rank} type={type} />
        <Link
          onClick={() =>
            onViewDetailsClick?.(
              `product_list_attribute_top_3_${type?.toLowerCase()}`
            )
          }
          styleName="container-link"
          to={url}
        >
          <div styleName="tile-content">
            {imageUrl && !rearCode && (
              <ProductImage
                ariaLabel={name || ''}
                height={120}
                imageFormatType={
                  process.env.__FF_PRODUCT_IMAGE_FORMATS_AUTO_SYNC__ === 'true'
                    ? 'PRODUCT_X_SMALL'
                    : 'LIST'
                }
                imageUrl={imageUrl}
                styleName="product-image"
                width={120}
              />
            )}
            <div styleName="text-container">
              <div styleName="brand-name-container">
                <Typography
                  size="extraSmall"
                  styleName="brand"
                  tag="span"
                  weight="black"
                >
                  {brand}
                </Typography>
                <Typography styleName="name" tag="span" weight="black">
                  {name}
                </Typography>
              </div>
              <div styleName="tile-summary">
                <div styleName="price-compare-container">
                  <span styleName="price-container">
                    <Typography
                      size="small"
                      styleName={rearCode ? 'staggered-price' : 'price'}
                      tag="span"
                    >
                      {rearCode && <span styleName="price-label">Front:</span>}
                      {price && (
                        <Price
                          className="price"
                          mapPrice={mapPrice}
                          price={price}
                          priceRange={priceRange}
                          unitName={unitName}
                        />
                      )}
                    </Typography>
                    {rearCode && rearPrice && (
                      <Typography
                        size="small"
                        styleName="staggered-price-rear"
                        tag="span"
                      >
                        <span styleName="price-label">Rear:</span>
                        <Price
                          className="price"
                          mapPrice={rearMapPrice}
                          price={rearPrice}
                          priceRange={rearPriceRange}
                          unitName={unitName}
                        />
                      </Typography>
                    )}
                  </span>

                  {!rearProduct && (
                    <CompareCheckbox
                      hideSelectMoreText
                      product={product}
                      quantity={quantity}
                      rearQuantity={rearQuantity}
                    />
                  )}
                </div>
                {priceSubtext && (
                  <Typography
                    size="extraSmall"
                    styleName="price-subtext"
                    tag="span"
                  >
                    {priceSubtext}
                  </Typography>
                )}

                <div styleName="details-container">
                  {url && (
                    <Typography
                      size="small"
                      styleName="view-details"
                      tag="span"
                      weight="bold"
                    >
                      View Details
                    </Typography>
                  )}

                  {rearProduct && (
                    <CompareCheckbox
                      hideSelectMoreText
                      product={product}
                      quantity={quantity}
                      rearQuantity={rearQuantity}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
}

export default TopThreeTile;
